import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserTabProps } from './types'
import Icon from '../../Icon'
import { useNotification } from '../../modules/Notification'
import {
  Card, CardTitle, HeaderRow, AvatarTile, AvatarImg, UserNameLabel, UserName,
  DetailGrid, HeaderGrid, DetailLabel, DetailValue, MutedValue, Badge, KeyValueRow, IconButton, AdvancedKey, ExternalLink,
} from './userStyles'
import {
  fullName, providerMeta, languageFlag, languageName, formatDate, approxCreatedMs, approxLastOnlineMs,
} from './userHelpers'

const LOGIN_PROVIDERS: { key: 'google' | 'password' | 'apple' | 'facebook'; label: string }[] = [
  { key: 'google', label: 'Google' },
  { key: 'password', label: 'Password' },
  { key: 'apple', label: 'Apple' },
  { key: 'facebook', label: 'Facebook' },
]

// Fields we render explicitly; everything else on the node surfaces in the "advanced" catch-all.
const KNOWN_TOP_KEYS = new Set([
  'user_uid', 'email', 'first_name', 'last_name', 'nickname', 'birth_date', 'photo_url',
  'provider_enum', 'accepted_terms_version', 'charger_id', 'provider_logins', 'platform_logins',
  'fcm_tokens', 'fcm_token', 'devices', 'live_activity', 'chargers', 'preferences',
  'login_google', 'login_email', 'chargerid',
])

export default function UserGeneralInformation({ user, uid }: UserTabProps) {
  const { t } = useTranslation()
  const { addNotification } = useNotification()
  const [photoError, setPhotoError] = useState(false)

  const provider = providerMeta(user.provider_enum)
  const flag = languageFlag(user.preferences?.language)
  const photo = user.photo_url

  const copy = (value?: string) => {
    if (!value) return
    navigator.clipboard.writeText(value)
    addNotification(t('user_copied'), 'TEXT', 'normal', 1000)
  }

  // Catch-all: any unmapped top-level key (e.g. preferences.network on a few users, or future keys).
  const advanced = Object.entries(user).filter(([k, v]) => !KNOWN_TOP_KEYS.has(k) && v != null && typeof v !== 'object')

  return (
    <>
      {/* Header card */}
      <Card>
        <HeaderRow>
          <AvatarTile>
            {photo && !photoError ? <AvatarImg src={photo} alt="" onError={() => setPhotoError(true)} /> : <Icon path="chargers.user" width="26px" height="26px" color="white" />}
          </AvatarTile>
          <div>
            <UserNameLabel>{t('user_userName')}</UserNameLabel>
            <UserName>{fullName(user)}</UserName>
          </div>
        </HeaderRow>
        <HeaderGrid>
          <div>
            <DetailLabel>{t('user_userUid')}</DetailLabel>
            <DetailValue>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.user_uid || uid}</span>
              <IconButton aria-label={t('user_copy')} onClick={() => copy(user.user_uid || uid)}>
                <Icon path="general.copy" width="14px" height="14px" color="gray_500" style={{ flexShrink: 0 }} />
              </IconButton>
            </DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_providersEmail')}</DetailLabel>
            <DetailValue><Icon path={provider.icon} width="16px" height="16px" /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email || '-'}</span></DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_termsAccepted')}</DetailLabel>
            <DetailValue>{formatDate(user.accepted_terms_version)}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_language')}</DetailLabel>
            <DetailValue>
              {flag && <Icon path={flag} width="18px" height="18px" />}
              {languageName(user.preferences?.language)}
            </DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_created')}</DetailLabel>
            <DetailValue>{formatDate(approxCreatedMs(user))}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_lastOnline')}</DetailLabel>
            <DetailValue>{formatDate(approxLastOnlineMs(user))}</DetailValue>
          </div>
        </HeaderGrid>
      </Card>

      {/* Profile & account (RTDB) card */}
      <Card>
        <CardTitle>{t('user_profileAccountDetails')}</CardTitle>
        <DetailGrid>
          <div>
            <DetailLabel>{t('user_nickname')}</DetailLabel>
            <DetailValue>{user.nickname || '-'}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_name')}</DetailLabel>
            <DetailValue>{[user.last_name, user.first_name].filter(Boolean).join(' / ') || '-'}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_birthDate')}</DetailLabel>
            <DetailValue>{formatDate(user.birth_date)}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_termsVersion')}</DetailLabel>
            <DetailValue>{formatDate(user.accepted_terms_version)}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_primaryProvider')}</DetailLabel>
            <DetailValue><Badge $tone="neutral">{user.provider_enum || '-'}</Badge></DetailValue>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <DetailLabel>{t('user_linkedLogins')}</DetailLabel>
            <DetailValue style={{ flexWrap: 'wrap' }}>
              {LOGIN_PROVIDERS.map(p => {
                const on = !!(user.provider_logins && user.provider_logins[p.key])
                return <Badge key={p.key} $tone={on ? 'success' : 'neutral'}>{p.key === 'password' ? t('user_loginPassword') : p.label}{on ? ' ✓' : ''}</Badge>
              })}
            </DetailValue>
          </div>
          <div>
            <DetailLabel>{t('user_primaryCharger')}</DetailLabel>
            <DetailValue>{user.charger_id || user.chargerid || '-'}</DetailValue>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <DetailLabel>{t('user_photoUrl')}</DetailLabel>
            <DetailValue>{user.photo_url ? <ExternalLink href={user.photo_url} target="_blank" rel="noopener noreferrer">{user.photo_url.slice(0, 18)}…</ExternalLink> : <MutedValue>{t('user_notProvided')}</MutedValue>}</DetailValue>
          </div>
        </DetailGrid>

        {advanced.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <DetailLabel>{t('user_advanced')}</DetailLabel>
            {advanced.map(([k, v]) => (
              <KeyValueRow key={k}><AdvancedKey>{k}</AdvancedKey><span>{String(v)}</span></KeyValueRow>
            ))}
          </div>
        )}
      </Card>
    </>
  )
}
